import { Injectable, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { TranslateService } from '@ngx-translate/core'
import { combineLatest } from 'rxjs'
import { filter } from 'rxjs/operators'
import { AppUser } from '../../domain/user/app-user.model'
import { NavigationService } from './core/services/navigation.service'
import { PixelHelperService } from './core/services/pixel-helper.service'
import { ShopService } from './core/services/shop.service'
import { SnackbarService } from './core/services/snackbar.service'
import { ValidationErrorResolversService } from './core/services/validation-error-resolvers.service'
import { GdprService } from './gdpr/gdpr.service'
import { AuthService } from './modules/auth/auth.service'
import { ShopSettingsService } from './modules/auth/shop-settings.service'
import { UserTermsDialog } from './modules/user-terms/user-terms.dialog'
import { DestroyAware, untilDestroyed } from './shared/utils/destroy-aware.decorator'

@DestroyAware()
@Injectable({
	providedIn: 'root',
})
export class AppInitService {
	private readonly validationErrorResolvers = inject(ValidationErrorResolversService)
	private readonly shopSettingsService = inject(ShopSettingsService)
	private readonly shopService = inject(ShopService)
	private readonly navigationService = inject(NavigationService)
	private readonly authService = inject(AuthService)
	private readonly dialog = inject(MatDialog)
	private readonly translateService = inject(TranslateService)
	private readonly gdprService = inject(GdprService)
	private readonly pixelHelperService = inject(PixelHelperService)
	private readonly snackbarService = inject(SnackbarService)

	init(): void {
		this.initTranslations()

		this.gdprService.init()

		this.pixelHelperService.init()

		this.validationErrorResolvers.registerAll()

		combineLatest([this.shopSettingsService.shopSettingsChanges$, this.shopService.isPublicShop$])
			.pipe(untilDestroyed(this))
			.subscribe(([settings, isPublicShop]) => {
				if (isPublicShop && !settings.stripeApiKey) {
					this.navigationService.goToPageNotFound()
					return
				}
				this.shopSettingsService.applySettings(settings)
			})

		this.authService.appUserChanges$
			.pipe(
				untilDestroyed(this),
				filter((user) => !!(user && !user.agreed)),
			)
			.subscribe((user) => {
				this.showUserTermsDialog(user as AppUser)
			})
		// this.checkBrowser()
	}

	private initTranslations(): void {
		this.translateService.addLangs(['ro', 'en', 'it', 'de'])
		const browserLang = this.translateService.getBrowserLang()
		const langPref = localStorage.getItem('langPref')
		if (langPref) {
			this.translateService.use(langPref)
		} else {
			this.translateService.use(browserLang?.match(/ro|en|it|de/) ? browserLang : 'en')
		}
	}

	private showUserTermsDialog(user: AppUser): void {
		const dialogRef = this.dialog.open(UserTermsDialog, {
			data: user,
			position: { top: '2%' },
			width: '700px',
			disableClose: true,
		})
		dialogRef.afterClosed().subscribe()
	}

	//check if browser is safari
	private checkBrowser() {
		if (navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1) {
			this.snackbarService.showErrorMessage('We recommend using Chrome browser for the best experience.', 15000)
		}
	}
}
